<template>
  <div>
    <AdminHeader />

    <!-- Page Header -->
    <div class="page-header">
      <div>
        <h5 class="page-title">Business Locations</h5>
        <p class="page-sub">Manage all your business branches</p>
      </div>

      <div class="page-actions">
        <div class="search-wrap">
          <font-awesome-icon icon="search" class="search-icon" />
          <input
            type="text"
            class="search-input"
            placeholder="Search locations..."
            v-model="tableData.search"
            @input="tableData.page = 1; getDetails()"
          />
        </div>

        <select v-model="tableData.status" @change="tableData.page = 1; getDetails()" class="filter-select">
          <option :value="[0,1]">All Statuses</option>
          <option :value="[1]">Active</option>
          <option :value="[0]">Inactive</option>
        </select>

        <!-- Export -->
        <div class="dropdown" style="position:relative;">
          <button class="btn-outline" @click="exportOpen = !exportOpen">
            <font-awesome-icon icon="download" /> Export
          </button>
          <ul class="dropdown-menu" :class="{ show: exportOpen }" style="right:0;left:auto;">
            <li>
              <a class="dropdown-item" href="#" @click.prevent="openExportModal('pdf')">
                <font-awesome-icon icon="file-pdf" class="text-danger me-2" /> PDF
              </a>
            </li>
            <li>
              <a class="dropdown-item" href="#" @click.prevent="openExportModal('csv')">
                <font-awesome-icon icon="file-csv" class="text-success me-2" /> CSV
              </a>
            </li>
          </ul>
        </div>

        <button class="btn-primary-action" :disabled="permission.create_location != 1"
          @click="permission.create_location == 1 ? openCreate() : null">
          <font-awesome-icon icon="plus" /> Create Location
        </button>
      </div>
    </div>

    <!-- Data card -->
    <div class="data-card">

      <!-- Loading -->
      <div v-if="loading" class="state-block">
        <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
        <p class="mt-3 text-muted-cell">Loading locations...</p>
      </div>

      <!-- Empty -->
      <div v-else-if="details.length === 0" class="state-block">
        <div class="state-icon state-icon--blue"><font-awesome-icon icon="university" /></div>
        <p class="state-title">No locations found</p>
        <p class="state-sub">Create your first business location to get started.</p>
        <button class="btn-primary-action mt-3" :disabled="permission.create_location != 1"
           @click="permission.create_location == 1 ? openCreate() : null">
          <font-awesome-icon icon="plus" /> Create Location
        </button>
      </div>

      <template v-else>

        <!-- Toolbar -->
        <div class="table-toolbar">
          <span class="table-count">
            Showing <strong>{{ pagination.from }}–{{ pagination.to }}</strong>
            of <strong>{{ pagination.total }}</strong>
          </span>
          <div class="toolbar-right">
            <label class="table-count">Rows:</label>
            <select v-model.number="tableData.length" @change="tableData.page = 1; getDetails()" class="rows-select">
              <option v-for="n in [5,10,20,50,100,200,500,1000]" :key="n" :value="n">{{ n }}</option>
            </select>
          </div>
        </div>

        <!-- Table -->
        <div class="table-wrap">
          <table class="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Contact Email</th>
                <th>Contact Phone</th>
                <th>Address</th>
                <th>Location Head</th>
                <th>Status</th>
                <th class="col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(location, index) in details" :key="location.id">

                <td class="text-muted-cell">{{ pagination.from + index }}</td>

                <td>
                  <div class="bank-cell">
                    <div class="bank-icon"><font-awesome-icon icon="building" /></div>
                    <span class="bank-name">{{ location.name }}</span>
                  </div>
                </td>
                <td class="text-muted-cell">
                  <div>{{ location.email }}</div>
                </td>
                <td class="text-muted-cell">
                  <div>{{ location.phone_number }}</div>
                  <div v-if="location.phone_number2" class="text-xs">{{ location.phone_number2 }}</div>
                </td>
                <td class="text-muted-cell">
                  <div>{{ location.address }}</div>
                  <div v-if="location.post_code" class="text-xs">{{ location.post_code }}</div>
                </td>
                <td class="text-muted-cell">
                  <div v-if="location?.employee" class="text-xs">{{ location?.employee?.first_name }} {{ location?.employee?.last_name }}</div>
                </td>
                <td>
                  <label class="toggle-switch">
                    <input
                      type="checkbox"
                      :true-value="1"
                      :false-value="0"
                      v-model="statuses[index]"
                      :disabled="permission.update_location != 1"
                      @change="permission.update_location == 1 ? updateLocationStatus(location.id) : null"
                    />
                    <span class="toggle-track"><span class="toggle-thumb"></span></span>
                    <span class="toggle-label" :class="statuses[index] ? 'on' : 'off'">
                      {{ statuses[index] ? 'Active' : 'Inactive' }}
                    </span>
                  </label>
                </td>

                <td class="col-actions">
                  <div class="actions-cell">
                    <button
                      class="action-btn action-btn--edit"
                      :disabled="permission.update_location != 1"
                      @click="permission.update_location == 1 ? openEdit(location) : null"
                      title="Edit"
                    >
                      <font-awesome-icon icon="pen" />
                    </button>
                    <button
                      class="action-btn action-btn--delete"
                      title="Delete"
                      :disabled="permission.delete_location != 1"
                      @click="permission.delete_location == 1 ? openDelete(location) : null"
                    >
                      <font-awesome-icon icon="trash" />
                    </button>
                  </div>
                </td>

              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div v-if="pagination.lastPage > 1" class="pagination-bar">
          <span class="pagination-info">Showing {{ pagination.from }}–{{ pagination.to }} of {{ pagination.total }}</span>
          <div class="pagination-controls">
            <button class="pg-btn" :disabled="pagination.currentPage <= 1" @click="tableData.page = 1; getDetails()">«</button>
            <button class="pg-btn" :disabled="pagination.currentPage <= 1" @click="tableData.page = pagination.currentPage - 1; getDetails()">‹ Prev</button>
            <span class="pg-pages">
              <button v-for="p in pageRange" :key="p" class="pg-num" :class="{ 'pg-num--active': p === pagination.currentPage }" @click="tableData.page = p; getDetails()">{{ p }}</button>
            </span>
            <button class="pg-btn" :disabled="pagination.currentPage >= pagination.lastPage" @click="tableData.page = pagination.currentPage + 1; getDetails()">Next ›</button>
            <button class="pg-btn" :disabled="pagination.currentPage >= pagination.lastPage" @click="tableData.page = pagination.lastPage; getDetails()">»</button>
          </div>
        </div>

      </template>
    </div>

    <!-- ── Create Modal ── -->
    <div class="modal-overlay" :class="{ open: createOpen }" @click.self="closeCreate">
      <div class="modal-box">
        <div class="modal-header-styled">
          <span class="modal-title-styled">Create Location</span>
          <button class="modal-close-btn" @click="closeCreate"><font-awesome-icon icon="times" /></button>
        </div>
        <form @submit.prevent="createLocation">
          <div class="modal-body-styled">
            <div class="">
              <div class="form-group-styled">
                <label class="form-label-styled">Location Name <span class="req">*</span></label>
                <input v-model="name" type="text" class="form-input-styled" placeholder="Enter location name" required />
              </div>
            </div>
            <div class="">
              <div class="form-group-styled">
                <label class="form-label-styled">Address <span class="req">*</span></label>
                <textarea v-model="address" class="form-input-styled" placeholder="Enter Location Address" required></textarea>
              </div>
            </div>
            <div class="fields-row">
              <div class="form-group-styled">
                <label class="form-label-styled">Address Post Code</label>
                <input v-model="post_code" type="text" class="form-input-styled" placeholder="Enter post code" />
              </div>
              <div class="form-group-styled" style="margin-bottom:0;">
                <label class="form-label-styled">Contact Email</label>
                <input v-model="email" type="email" class="form-input-styled" placeholder="Enter email" />
              </div>
            </div>
            <div class="fields-row">
              <div class="form-group-styled">
                <label class="form-label-styled">Contact Phone Number</label>
                <input v-model="phone_number" type="text" class="form-input-styled" placeholder="Enter phone number" inputmode="numeric" />
              </div>
              <div class="form-group-styled" style="margin-bottom:0;">
                <label class="form-label-styled">Contact Phone Number 2</label>
                <input v-model="phone_number2" type="text" class="form-input-styled" placeholder="Enter phone number 2" inputmode="numeric" />
              </div>
            </div>
            <div class="">
              <div class="form-group-styled">
                <label class="form-label-styled">Head of Location</label>
                <search-select
                  v-model="employee_id"
                  url="payroll/employees/active"
                  query-param="search"
                  label-key="full_name"
                  sublabel-key="employee_no"
                  value-key="id"
                  response-key="data"
                  placeholder="Select employee..."
                  search-placeholder="Type name or number…"
                  :min-chars="1"
                />
              </div>
            </div>
            <div class="coords-section">
              <div class="coords-header">
                <font-awesome-icon icon="map-marker-alt" class="coords-header-icon" />
                <div>
                  <p class="coords-title">Site Coordinates</p>
                  <p class="coords-sub">Used for location verification</p>
                </div>
                <button type="button" class="btn-gps" @click="captureGps('create')" :disabled="gpsLoading">
                  <font-awesome-icon :icon="gpsLoading ? 'spinner' : 'crosshairs'" :spin="gpsLoading" />
                  {{ gpsLoading ? 'Getting…' : 'Use GPS' }}
                </button>
              </div>
              <p v-if="gpsError" class="gps-error">{{ gpsError }}</p>
              <div class="fields-row">
                <div class="form-group-styled">
                  <label class="form-label-styled">Latitude</label>
                  <input type="number" step="0.0000001" class="form-input-styled" v-model="latitude" placeholder="e.g. 51.5074" />
                </div>
                <div class="form-group-styled">
                  <label class="form-label-styled">Longitude</label>
                  <input type="number" step="0.0000001" class="form-input-styled" v-model="longitude" placeholder="e.g. -0.1278" />
                </div>
              </div>
              <a v-if="latitude && longitude"
                :href="`https://www.openstreetmap.org/?mlat=${latitude}&mlon=${longitude}&zoom=16`"
                target="_blank" rel="noopener" class="map-preview-link">
                <font-awesome-icon icon="external-link-alt" /> View on map
              </a>
            </div>
          </div>
          <div class="modal-footer-styled">
            <button type="button" class="btn-ghost" @click="closeCreate">Cancel</button>
            <button type="submit" class="btn-primary-action" :disabled="!name.trim() || !address.trim() || crudLoading">
              <font-awesome-icon v-if="crudLoading" icon="spinner" spin class="me-1" />
              {{ crudLoading ? 'Creating...' : 'Create Location ' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- ── Edit Modal ── -->
    <div class="modal-overlay" :class="{ open: editOpen }" @click.self="closeEdit">
      <div class="modal-box">
        <div class="modal-header-styled">
          <span class="modal-title-styled">Edit Location</span>
          <button class="modal-close-btn" @click="closeEdit"><font-awesome-icon icon="times" /></button>
        </div>
        <form @submit.prevent="updateLocation">
          <div class="modal-body-styled">
            <div class="">
              <div class="form-group-styled">
                <label class="form-label-styled">Location Name <span class="req">*</span></label>
                <input v-model="edit_name" type="text" class="form-input-styled" placeholder="Enter location name" required />
              </div>
            </div>
            <div class="">
              <div class="form-group-styled">
                <label class="form-label-styled">Address</label>
                <textarea v-model="edit_address" class="form-input-styled" placeholder="Enter Location Address" required></textarea>
              </div>
            </div>
            <div class="fields-row">
              <div class="form-group-styled">
                <label class="form-label-styled">Address Post Code</label>
                <input v-model="edit_post_code" type="text" class="form-input-styled" placeholder="Enter post code" />
              </div>
              <div class="form-group-styled" style="margin-bottom:0;">
                <label class="form-label-styled">Contact Email</label>
                <input v-model="edit_email" type="email" class="form-input-styled" placeholder="Enter email" />
              </div>
            </div>
            <div class="fields-row">
              <div class="form-group-styled">
                <label class="form-label-styled">Contact Phone Number</label>
                <input v-model="edit_phone_number" type="text" class="form-input-styled" placeholder="Enter phone number" inputmode="numeric" />
              </div>
              <div class="form-group-styled" style="margin-bottom:0;">
                <label class="form-label-styled">Contact Phone Number 2</label>
                <input v-model="edit_phone_number2" type="text" class="form-input-styled" placeholder="Enter phone number 2" inputmode="numeric" />
              </div>
            </div>
            <div class="">
              <div class="form-group-styled">
                <label class="form-label-styled">Head of Location</label>
                <search-select
                  v-model="edit_employee_id"
                  url="payroll/employees/active"
                  query-param="search"
                  label-key="full_name"
                  sublabel-key="employee_no"
                  value-key="id"
                  response-key="data"
                  placeholder="Select employee..."
                  search-placeholder="Type name or number…"
                  :min-chars="1"
                />
              </div>
            </div>
            <div class="coords-section">
              <div class="coords-header">
                <font-awesome-icon icon="map-marker-alt" class="coords-header-icon" />
                <div>
                  <p class="coords-title">Site Coordinates</p>
                  <p class="coords-sub">Used for location verification</p>
                </div>
                <button type="button" class="btn-gps" @click="captureGps('edit')" :disabled="gpsLoading">
                  <font-awesome-icon :icon="gpsLoading ? 'spinner' : 'crosshairs'" :spin="gpsLoading" />
                  {{ gpsLoading ? 'Getting…' : 'Use GPS' }}
                </button>
              </div>
              <p v-if="gpsError" class="gps-error">{{ gpsError }}</p>
              <div class="fields-row">
                <div class="form-group-styled">
                  <label class="form-label-styled">Latitude</label>
                  <input type="number" step="0.0000001" class="form-input-styled" v-model="edit_latitude" placeholder="e.g. 51.5074" />
                </div>
                <div class="form-group-styled">
                  <label class="form-label-styled">Longitude</label>
                  <input type="number" step="0.0000001" class="form-input-styled" v-model="edit_longitude" placeholder="e.g. -0.1278" />
                </div>
              </div>
              <a v-if="edit_latitude && edit_longitude"
                :href="`https://www.openstreetmap.org/?mlat=${edit_latitude}&mlon=${edit_longitude}&zoom=16`"
                target="_blank" rel="noopener" class="map-preview-link">
                <font-awesome-icon icon="external-link-alt" /> View on map
              </a>
            </div>
          </div>
          <div class="modal-footer-styled">
            <button type="button" class="btn-ghost" @click="closeEdit">Cancel</button>
            <button type="submit" class="btn-primary-action" :disabled="!edit_name.trim() || crudLoading">
              <font-awesome-icon v-if="crudLoading" icon="spinner" spin class="me-1" />
              {{ crudLoading ? 'Saving...' : 'Save Changes' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- ── Delete Modal ── -->
    <div class="modal-overlay" :class="{ open: deleteOpen }" @click.self="closeDelete">
      <div class="modal-box">
        <div class="modal-body-styled" style="padding:32px 24px 24px; text-align:center;">
          <div class="delete-icon-wrap"><font-awesome-icon icon="trash" style="font-size:26px;" /></div>
          <div class="delete-title">Delete Location</div>
          <p class="delete-sub">
            Are you sure you want to delete <strong>{{ edit_name }}</strong>? This action cannot be undone.
          </p>
        </div>
        <div class="modal-footer-styled" style="justify-content:center; gap:12px;">
          <button type="button" class="btn-ghost" @click="closeDelete">Cancel</button>
          <button type="button" class="btn-danger-action" @click="deleteLocation">Yes, Delete</button>
        </div>
      </div>
    </div>

    <!-- Backdrop -->
    <div v-if="createOpen || editOpen || deleteOpen" class="modal-backdrop-custom" />

    <!-- ── Export Column Picker ──────────────────────────────────────────── -->
    <transition name="fade">
      <div v-if="showExportModal" class="col-picker-backdrop" @click.self="showExportModal = false">
        <div class="col-picker-modal">
          <div class="col-picker-header">
            <div>
              <h3 class="col-picker-title">Select Columns</h3>
              <p class="col-picker-sub">Choose what to include in your {{ pendingExportFmt.toUpperCase() }} export</p>
            </div>
            <button class="col-picker-close" @click="showExportModal = false">
              <font-awesome-icon icon="times" />
            </button>
          </div>
          <div class="col-picker-actions">
            <button class="col-picker-link" @click="selectedExportCols = exportColumnDefs.map(c => c.key)">Select all</button>
            <span class="col-picker-sep">·</span>
            <button class="col-picker-link" @click="selectedExportCols = []">Deselect all</button>
            <span class="col-picker-count">{{ selectedExportCols.length }} of {{ exportColumnDefs.length }} selected</span>
          </div>
          <div class="col-picker-grid">
            <label v-for="col in exportColumnDefs" :key="col.key" class="col-picker-item">
              <input type="checkbox" :value="col.key" v-model="selectedExportCols" class="col-picker-check" />
              <span class="col-picker-label">{{ col.label }}</span>
            </label>
          </div>
          <div class="col-picker-footer">
            <button class="btn-ghost-cp" @click="showExportModal = false">Cancel</button>
            <button class="btn-primary-cp"
              :disabled="selectedExportCols.length === 0 || exportLoading"
              @click="doExport()">
              <font-awesome-icon
                :icon="exportLoading ? 'spinner' : pendingExportFmt === 'pdf' ? 'file-pdf' : 'file-csv'"
                :spin="exportLoading" />
              Export {{ pendingExportFmt.toUpperCase() }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';
import AdminHeader from "./administrationheadertab.vue";
import SearchSelect from "../../../components/SearchSelect.vue";
import { useStore } from 'vuex';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

export default {
  name: 'BusinessLocations',
  components: { AdminHeader, SearchSelect },

  data() {
    return {
      details:  [],
      statuses: [],
      location_id:  '',
      loading:     false,
      crudLoading: false,
      exportOpen:     false,
      exportLoading:  false,
      showExportModal: false,
      pendingExportFmt: 'csv',
      selectedExportCols: [],
      exportColumnDefs: [
        { key: 'name',    label: 'Name',           pdfWidth: 130 },
        { key: 'email',   label: 'Contact Email',  pdfWidth: 170 },
        { key: 'phone',   label: 'Phone',          pdfWidth: 100 },
        { key: 'address', label: 'Address',        pdfWidth: 170 },
        { key: 'head',    label: 'Location Head',  pdfWidth: 130 },
        { key: 'status',  label: 'Status',         pdfWidth: 61 },
      ],

      // Create fields
      name: '', address: '', post_code: '', email: '', phone_number: '', phone_number2: '', employee_id: '',
      latitude: '', longitude: '',
      // Edit fields
      edit_name: '', edit_address: '', edit_post_code: '', edit_email: '', edit_phone_number: '', edit_phone_number2: '', edit_employee_id: '',
      edit_latitude: '', edit_longitude: '',
      // GPS
      gpsLoading: false, gpsError: '',

      createOpen: false,
      editOpen:   false,
      deleteOpen: false,

      tableData: { page: 1,
        draw: 0, length: 5, search: '',
        column: 0, dir: 'desc', status: [0, 1],
      },

      pagination: {
        lastPage: '', currentPage: '', total: '',
        lastPageUrl: '', nextPageUrl: '', prevPageUrl: '',
        from: 1, to: '',
      },

      toast: useToast(),
      store: useStore(),
    };
  },

  computed: {
    permission() {
      return this.store.state.getCurrentUserPermission;
    },
    pageRange() {
      if (!this.pagination.lastPage) return [];
      const cur = this.pagination.currentPage, last = this.pagination.lastPage;
      const range = [];
      for (let i = Math.max(1, cur - 2); i <= Math.min(last, cur + 2); i++) range.push(i);
      return range;
    },
  },

  methods: {
    // ── Modal helpers ────────────────────────────────────────────────
    openCreate() {
      this.name = ''; this.address = ''; this.post_code = ''; this.email = '';
      this.phone_number = ''; this.phone_number2 = ''; this.employee_id = '';
      this.latitude = ''; this.longitude = '';
      this.gpsError = '';
      this.createOpen = true;
    },
    closeCreate() { this.createOpen = false; },

    openEdit(location) {
      this.location_id = location.id;
      this.edit_name        = location.name;
      this.edit_address     = location.address;
      this.edit_post_code   = location.post_code;
      this.edit_email       = location.email;
      this.edit_phone_number  = location.phone_number;
      this.edit_phone_number2 = location.phone_number2;
      this.edit_employee_id   = location.employee_id;
      this.edit_latitude    = location.latitude  ?? '';
      this.edit_longitude   = location.longitude ?? '';
      this.gpsError = '';
      this.editOpen = true;
    },
    closeEdit() { this.editOpen = false; },

    openDelete(location) {
      this.location_id = location.id;
      this.edit_name = location.name;
      this.deleteOpen = true;
    },
    closeDelete() { this.deleteOpen = false; },

    // ── Export ───────────────────────────────────────────────────────
    openExportModal(format) {
      this.pendingExportFmt   = format;
      this.selectedExportCols = this.exportColumnDefs.map(c => c.key);
      this.showExportModal    = true;
      this.exportOpen         = false;
    },

    getRowValue(row, key) {
      switch (key) {
        case 'name':    return row.name || '—';
        case 'email':   return row.email || '—';
        case 'phone':   return row.phone_number || '—';
        case 'address': return row.address || '—';
        case 'head':    return row.employee ? `${row.employee.first_name || ''} ${row.employee.last_name || ''}`.trim() : '—';
        case 'status':  return row.is_active ? 'Active' : 'Inactive';
        default:        return '—';
      }
    },

    async doExport() {
      if (!this.selectedExportCols.length) return;
      this.exportLoading   = true;
      this.showExportModal = false;
      try {
        const params = { ...this.tableData, page: 1, length: 10000 };
        const res    = await axios.get('admin/location', { params });
        const rows   = Array.isArray(res.data?.data?.data) ? res.data.data.data : [];
        const cols    = this.exportColumnDefs.filter(c => this.selectedExportCols.includes(c.key));
        const headers = cols.map(c => c.label);
        const body    = rows.map(row => cols.map(c => this.getRowValue(row, c.key)));
        const date    = new Date().toISOString().slice(0, 10);
        if (this.pendingExportFmt === 'csv') {
          const csv  = [headers, ...body]
            .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
            .join('\n');
          const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
          const url  = URL.createObjectURL(blob);
          const a    = document.createElement('a');
          a.href = url; a.download = `locations-${date}.csv`; a.click();
          URL.revokeObjectURL(url);
        } else {
          const doc       = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
          const usable    = 761;
          const totalW    = cols.reduce((s, c) => s + c.pdfWidth, 0);
          const scale     = usable / totalW;
          const colStyles = {};
          cols.forEach((c, i) => { colStyles[i] = { cellWidth: c.pdfWidth * scale }; });
          doc.setFontSize(18); doc.setFont('helvetica', 'bold'); doc.setTextColor(26, 43, 69);
          doc.text('Business Locations', 40, 46);
          doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(122, 144, 176);
          doc.text(`Exported ${new Date().toLocaleDateString('en-GB')}   ·   ${rows.length} record(s)`, 40, 62);
          doc.autoTable({
            startY: 78, head: [headers], body,
            styles: { fontSize: 7.5, cellPadding: 3.5, textColor: [26, 43, 69], overflow: 'ellipsize' },
            headStyles: { fillColor: [0, 74, 173], textColor: 255, fontStyle: 'bold', fontSize: 7.5 },
            alternateRowStyles: { fillColor: [248, 250, 252] },
            columnStyles: colStyles, tableWidth: usable, margin: { left: 40, right: 40 },
            didDrawPage(data) {
              doc.setFontSize(8); doc.setTextColor(192, 202, 214);
              doc.text(`Page ${data.pageNumber}`, doc.internal.pageSize.width / 2, doc.internal.pageSize.height - 16, { align: 'center' });
            },
          });
          doc.save(`locations-${date}.pdf`);
        }
      } catch { this.toast.error('Export failed. Please try again.'); }
      finally { this.exportLoading = false; }
    },

    // ── Data ─────────────────────────────────────────────────────────
    async getDetails(url = 'admin/location') {
      this.loading = true;
      this.tableData.draw++;
      try {
        const { data } = await axios.get(url, { params: this.tableData });
        this.details = data.data.data;
        this.statuses = this.details.map(b => b.is_active);
        this.configPagination(data.data);
      } catch (err) {
        this.toast.error(err.response?.data?.message ?? 'Could not load locations.');
      } finally {
        this.loading = false;
      }
    },

    async createLocation() {
      if (!this.name.trim()) return;
      this.crudLoading = true;
      try {
        await axios.post('/admin/location', {
          name: this.name,
          address: this.address,
          post_code: this.post_code,
          latitude:  this.latitude  || null,
          longitude: this.longitude || null,
          email:        this.email,
          phone_number:  this.phone_number,
          phone_number2: this.phone_number2,
          employee_id:   this.employee_id || null,
        });
        this.toast.success('Location created successfully.');
        this.closeCreate();
        this.getDetails();
      } catch (err) {
        const status = err.response?.status;
        if (status === 422) this.toast.warning(err.response.data.message?.[0] ?? 'Invalid input.');
        else this.toast.error('An error occurred. Please try again.');
      } finally {
        this.crudLoading = false;
      }
    },

    captureGps(target) {
      if (!navigator.geolocation) {
        this.gpsError = 'Geolocation is not supported by your browser'; return;
      }
      this.gpsLoading = true;
      this.gpsError   = '';
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const lat = parseFloat(pos.coords.latitude.toFixed(7));
          const lng = parseFloat(pos.coords.longitude.toFixed(7));
          if (target === 'edit') { this.edit_latitude = lat; this.edit_longitude = lng; }
          else                   { this.latitude = lat;      this.longitude = lng; }
          this.gpsLoading = false;
        },
        () => {
          this.gpsError   = 'Could not get location. Please enter coordinates manually.';
          this.gpsLoading = false;
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    },

    async updateLocation() {
      if (!this.edit_name.trim()) return;
      this.crudLoading = true;
      try {
        await axios.put('/admin/location/' + this.location_id, {
          name: this.edit_name,
          address: this.edit_address,
          post_code: this.edit_post_code,
          latitude:  this.edit_latitude  || null,
          longitude: this.edit_longitude || null,
          email:        this.edit_email,
          phone_number:    this.edit_phone_number,
          phone_number2: this.edit_phone_number2,
          employee_id:   this.edit_employee_id || null,
        });
        this.toast.success('Location updated successfully.');
        this.closeEdit();
        this.getDetails();
      } catch (err) {
        const status = err.response?.status;
        if (status === 422) this.toast.warning(err.response.data.message ?? 'Invalid input. Please check and try again.');
        else if (status === 400) this.toast.error(err.response.data.message ?? 'This location cannot be edited.');
        else this.toast.error(err.response.data.message ?? 'An error occurred. Please try again.');
      } finally {
        this.crudLoading = false;
      }
    },

    async updateLocationStatus(id) {
      try {
        const { data } = await axios.put('admin/location/update-status/' + id);
        this.toast.success(data.message);
        this.getDetails();
      } catch (err) {
        const status = err.response?.status;
        if (status === 422) this.toast.warning('Invalid request.');
        else this.toast.error('Could not update status.');
      }
    },

    async deleteLocation() {
      try {
        await axios.delete(`/admin/location/${this.location_id}`);
        this.toast.success('Location deleted successfully.');
        this.closeDelete();
        this.getDetails();
      } catch (err) {
        this.toast.error(err.response?.data?.message ?? 'Location could not be deleted.');
      }
    },

    configPagination(data) {
      this.pagination.lastPage    = data.last_page;
      this.pagination.currentPage = data.current_page;
      this.pagination.total       = data.total;
      this.pagination.lastPageUrl = data.last_page_url;
      this.pagination.nextPageUrl = data.next_page_url;
      this.pagination.prevPageUrl = data.prev_page_url;
      this.pagination.from        = data.from;
      this.pagination.to          = data.to;
    },
  },

  mounted() {
    this.getDetails();
  },
};
</script>

<style scoped>
/* ── Page ────────────────────────────────────────────────────────────── */
.page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; margin-bottom:20px; }
.page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub     { font-size:13px; color:#7a90b0; margin:0; }
.page-actions { display:flex; align-items:center; flex-wrap:wrap; gap:8px; }

/* ── Search & Filter ─────────────────────────────────────────────────── */
.search-wrap  { position:relative; display:flex; align-items:center; }
.search-icon  { position:absolute; left:11px; color:#7a90b0; font-size:13px; pointer-events:none; }
.search-input { padding:8px 12px 8px 33px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; width:200px; outline:none; transition:border-color .15s; }
.search-input:focus { border-color:#004AAD; }
.filter-select { padding:8px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; cursor:pointer; }

/* ── Buttons ─────────────────────────────────────────────────────────── */
.btn-primary-action { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:8px 16px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; white-space:nowrap; }
.btn-primary-action:hover:not(:disabled) { background:#003b8e; }
.btn-primary-action:disabled { opacity:.55; cursor:not-allowed; }
.btn-ghost { display:inline-flex; align-items:center; gap:7px; background:#f4f7fb; color:#3d5068; border:1px solid #e4eaf2; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; cursor:pointer; transition:background .15s; }
.btn-ghost:hover { background:#e4eaf2; }
.btn-danger-action { display:inline-flex; align-items:center; gap:7px; background:#e03535; color:#fff; font-size:13px; font-weight:600; padding:9px 22px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; }
.btn-danger-action:hover { background:#c02828; }
.mt-3 { margin-top:12px; }
.me-1 { margin-right:4px; }

/* ── Data card ───────────────────────────────────────────────────────── */
.data-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }

/* ── States ──────────────────────────────────────────────────────────── */
.state-block      { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; }
.state-icon       { width:60px; height:60px; border-radius:16px; display:flex; align-items:center; justify-content:center; font-size:24px; margin-bottom:16px; }
.state-icon--blue { background:rgba(0,74,173,.08); color:#004AAD; }
.state-title      { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
.state-sub        { font-size:13px; color:#7a90b0; margin:0; }
.text-muted-cell  { color:#4a5e78; }

/* ── Table toolbar ───────────────────────────────────────────────────── */
.table-toolbar  { display:flex; align-items:center; justify-content:space-between; padding:14px 20px; border-bottom:1px solid #e4eaf2; flex-wrap:wrap; gap:10px; }
.table-count    { font-size:13px; color:#7a90b0; }
.table-count strong { color:#1a2b45; }
.toolbar-right  { display:flex; align-items:center; gap:8px; }
.rows-select    { border:1px solid #dde5f0; border-radius:7px; padding:5px 10px; font-size:13px; color:#1a2b45; background:#f8fafc; outline:none; cursor:pointer; }

/* ── Table ───────────────────────────────────────────────────────────── */
.table-wrap  { overflow-x:auto; }
.data-table  { width:100%; border-collapse:collapse; }
.data-table thead tr { background:#f8fafc; }
.data-table thead th { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:12px 16px; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
.data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .12s; }
.data-table tbody tr:last-child { border-bottom:none; }
.data-table tbody tr:hover { background:#f8fafc; }
.data-table tbody td { padding:12px 16px; font-size:13px; vertical-align:middle; }
.col-actions { width:90px; text-align:center; }

/* ── Bank cell ───────────────────────────────────────────────────────── */
.bank-cell { display:flex; align-items:center; gap:10px; }
.bank-icon { width:30px; height:30px; border-radius:7px; background:rgba(0,74,173,.08); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:12px; flex-shrink:0; }
.bank-name { font-size:13px; font-weight:500; color:#1a2b45; }

/* ── Badges ──────────────────────────────────────────────────────────── */
.code-badge { display:inline-block; font-family:monospace; font-size:12px; font-weight:700; background:#f4f7fb; color:#1a2b45; border:1px solid #e4eaf2; border-radius:6px; padding:2px 8px; }
.acct-badge { display:inline-block; font-family:monospace; font-size:12px; font-weight:600; color:#3d5068; background:#f8fafc; border:1px solid #e4eaf2; border-radius:6px; padding:2px 8px; }

/* ── Toggle switch ───────────────────────────────────────────────────── */
.toggle-switch { display:inline-flex; align-items:center; gap:8px; cursor:pointer; user-select:none; }
.toggle-switch input { display:none; }
.toggle-track  { position:relative; width:36px; height:20px; border-radius:20px; background:#d1d9e8; transition:background .2s; flex-shrink:0; }
.toggle-switch input:checked + .toggle-track { background:#16a34a; }
.toggle-thumb  { position:absolute; top:3px; left:3px; width:14px; height:14px; border-radius:50%; background:#fff; box-shadow:0 1px 3px rgba(0,0,0,.2); transition:transform .2s; }
.toggle-switch input:checked + .toggle-track .toggle-thumb { transform:translateX(16px); }
.toggle-label  { font-size:12px; font-weight:600; }
.toggle-label.on  { color:#16a34a; }
.toggle-label.off { color:#7a90b0; }

/* ── Action buttons ──────────────────────────────────────────────────── */
.actions-cell      { display:flex; align-items:center; justify-content:center; gap:6px; }
.action-btn        { width:30px; height:30px; border-radius:7px; border:1px solid #dde5f0; background:#fff; display:flex; align-items:center; justify-content:center; font-size:12px; color:#7a90b0; cursor:pointer; transition:all .15s; }
.action-btn--edit:hover   { background:#e8f4fd; color:#004AAD; border-color:#c5d6f5; }
.action-btn--delete:hover { background:#fff0f0; color:#e03535; border-color:#f5c6c6; }

/* ── Table footer ────────────────────────────────────────────────────── */
.table-footer { padding:14px 20px; border-top:1px solid #e4eaf2; display:flex; justify-content:flex-end; }
.pagination-bar      { display:flex; align-items:center; justify-content:space-between; padding:14px 20px; border-top:1px solid #e4eaf2; flex-wrap:wrap; gap:10px; }
.pagination-info     { font-size:13px; color:#7a90b0; }
.pagination-controls { display:flex; align-items:center; gap:4px; }
.pg-btn  { padding:6px 11px; border:1px solid #dde5f0; border-radius:7px; background:#fff; color:#1a2b45; font-size:13px; cursor:pointer; transition:all .12s; }
.pg-btn:hover:not(:disabled) { background:#f4f7fb; border-color:#c8d6e8; }
.pg-btn:disabled { opacity:.4; cursor:not-allowed; }
.pg-pages { display:flex; gap:3px; }
.pg-num  { min-width:34px; padding:6px 8px; border:1px solid #dde5f0; border-radius:7px; background:#fff; color:#1a2b45; font-size:13px; cursor:pointer; transition:all .12s; text-align:center; }
.pg-num:hover { background:#f4f7fb; border-color:#c8d6e8; }
.pg-num--active { background:#004AAD; border-color:#004AAD; color:#fff; font-weight:600; }

/* ── Modal ───────────────────────────────────────────────────────────── */
.modal-overlay         { position:fixed; inset:0; z-index:1050; display:flex; align-items:center; justify-content:center; opacity:0; pointer-events:none; transition:opacity .2s; }
.modal-overlay.open    { opacity:1; pointer-events:all; }
.modal-backdrop-custom { position:fixed; inset:0; z-index:1040; background:rgba(15,25,50,.45); backdrop-filter:blur(2px); }
.modal-box             { background:#fff; border-radius:14px; width:100%; max-width:500px; margin:16px; box-shadow:0 20px 60px rgba(0,0,0,.18); overflow:hidden; transform:translateY(16px); transition:transform .22s ease; z-index:1051; position:relative; }
.modal-overlay.open .modal-box { transform:translateY(0); }
.modal-header-styled   { padding:20px 24px 16px; border-bottom:1px solid #e4eaf2; display:flex; align-items:center; justify-content:space-between; }
.modal-title-styled    { font-size:16px; font-weight:700; color:#1a2b45; }
.modal-close-btn       { width:30px; height:30px; border-radius:7px; border:1px solid #dde5f0; background:transparent; cursor:pointer; display:flex; align-items:center; justify-content:center; color:#7a90b0; font-size:13px; transition:background .15s; }
.modal-close-btn:hover { background:#f4f7fb; }
.modal-body-styled     { padding:20px 24px; display:flex; flex-direction:column; gap:14px; }
.modal-footer-styled   { padding:16px 24px; border-top:1px solid #e4eaf2; display:flex; justify-content:flex-end; gap:10px; }

/* ── Modal form ──────────────────────────────────────────────────────── */
.fields-row { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
.form-group-styled  { display:flex; flex-direction:column; gap:6px; }
.form-label-styled  { font-size:13px; font-weight:600; color:#1a2b45; }
.req                { color:#e03535; }
.form-input-styled  { width:100%; padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; transition:border-color .15s, box-shadow .15s; font-family:inherit; }
.form-input-styled:focus { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); }

/* ── Delete modal ────────────────────────────────────────────────────── */
.delete-icon-wrap { width:64px; height:64px; border-radius:50%; background:#fff0f0; color:#e03535; display:flex; align-items:center; justify-content:center; margin:0 auto 16px; }
.delete-title     { font-size:17px; font-weight:700; color:#1a2b45; margin-bottom:8px; }
.delete-sub       { font-size:13px; color:#7a90b0; line-height:1.5; }

/* ── Coordinate section ──────────────────────────────────────────────── */
.coords-section { border:1px solid #e4eaf2; border-radius:10px; padding:14px 16px; display:flex; flex-direction:column; gap:12px; background:#f8fafc; }
.coords-header  { display:flex; align-items:flex-start; gap:10px; }
.coords-header-icon { font-size:16px; color:#004AAD; margin-top:3px; flex-shrink:0; }
.coords-title   { font-size:13px; font-weight:700; color:#1a2b45; margin:0 0 2px; }
.coords-sub     { font-size:11px; color:#7a90b0; margin:0; }
.btn-gps { margin-left:auto; flex-shrink:0; display:inline-flex; align-items:center; gap:6px; background:#dbeafe; color:#1d4ed8; border:1px solid #bfdbfe; border-radius:7px; padding:6px 12px; font-size:12px; font-weight:600; cursor:pointer; transition:background .15s; }
.btn-gps:hover:not(:disabled) { background:#c8e0f8; }
.btn-gps:disabled { opacity:.6; cursor:not-allowed; }
.gps-error { font-size:11px; color:#dc2626; margin:0; }
.map-preview-link { font-size:12px; color:#004AAD; text-decoration:none; display:inline-flex; align-items:center; gap:5px; }
.map-preview-link:hover { text-decoration:underline; }

/* ── Responsive ──────────────────────────────────────────────────────── */
@media (max-width:768px) {
  .page-header, .page-actions { flex-direction:column; align-items:stretch; }
  .search-input { width:100%; }
  .fields-row   { grid-template-columns:1fr; }
}

/* ── Export dropdown ─────────────────────────────────────────────────── */
.dropdown-menu { position:absolute; top:calc(100% + 6px); background:#fff; border:1px solid #e4eaf2; border-radius:10px; box-shadow:0 8px 24px rgba(0,0,0,.1); padding:6px; min-width:160px; z-index:200; display:none; }
.dropdown-menu.show { display:block; }
.dropdown-item { display:flex; align-items:center; gap:8px; padding:8px 12px; font-size:13px; border-radius:7px; color:#1a2b45; cursor:pointer; text-decoration:none; }
.dropdown-item:hover { background:#f4f7fb; }
.btn-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:8px 14px; border-radius:8px; cursor:pointer; white-space:nowrap; }
.btn-outline:hover { background:#f4f7fb; }

/* ── Column picker modal ──────────────────────────────────────────────── */
.col-picker-backdrop { position:fixed; inset:0; background:rgba(15,25,40,.45); z-index:1050; display:flex; align-items:center; justify-content:center; padding:20px; }
.col-picker-modal    { background:#fff; border-radius:14px; width:100%; max-width:480px; box-shadow:0 24px 60px rgba(0,0,0,.18); overflow:hidden; }
.col-picker-header   { display:flex; align-items:flex-start; justify-content:space-between; padding:20px 22px 14px; border-bottom:1px solid #f0f4f9; }
.col-picker-title    { font-size:15px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.col-picker-sub      { font-size:12px; color:#7a90b0; margin:0; }
.col-picker-close    { background:none; border:none; color:#7a90b0; font-size:15px; cursor:pointer; padding:4px; line-height:1; }
.col-picker-close:hover { color:#1a2b45; }
.col-picker-actions  { display:flex; align-items:center; gap:8px; padding:10px 22px; background:#f8fafc; font-size:12px; border-bottom:1px solid #f0f4f9; }
.col-picker-link     { background:none; border:none; color:#004AAD; font-size:12px; cursor:pointer; padding:0; font-weight:600; font-family:inherit; }
.col-picker-link:hover { text-decoration:underline; }
.col-picker-sep      { color:#c5d3e0; }
.col-picker-count    { margin-left:auto; color:#7a90b0; font-size:11.5px; }
.col-picker-grid     { display:grid; grid-template-columns:1fr 1fr; gap:2px; padding:14px 16px; max-height:280px; overflow-y:auto; }
.col-picker-item     { display:flex; align-items:center; gap:9px; padding:9px 10px; border-radius:8px; cursor:pointer; transition:background .1s; }
.col-picker-item:hover { background:#f4f7fb; }
.col-picker-check    { width:15px; height:15px; accent-color:#004AAD; cursor:pointer; flex-shrink:0; }
.col-picker-label    { font-size:13px; color:#1a2b45; user-select:none; }
.col-picker-footer   { display:flex; justify-content:flex-end; gap:8px; padding:14px 20px; border-top:1px solid #f0f4f9; }
.btn-ghost-cp   { display:inline-flex; align-items:center; gap:7px; background:#f4f7fb; color:#3d5068; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; border:1px solid #e4eaf2; cursor:pointer; font-family:inherit; }
.btn-ghost-cp:hover { background:#e4eaf2; }
.btn-primary-cp { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; }
.btn-primary-cp:hover:not(:disabled) { background:#003b8e; }
.btn-primary-cp:disabled { opacity:.45; cursor:not-allowed; }
.fade-enter-active, .fade-leave-active { transition:opacity .15s ease; }
.fade-enter, .fade-leave-to { opacity:0; }
</style>